import { useUiText } from "../../locale/UiLocale";
import { forwardRef, useMemo } from "react";
import { useFieldContext } from "../Field/Field";
import { assignRef } from "./assignRef";
import { SEARCH_THRESHOLD } from "./filterOptions";
import { optionsFromChildren } from "./optionsFromChildren";
import { optionId, type SelectOption } from "./selectOption";
import type { SelectProps } from "./selectProps";
import { SelectListbox } from "./SelectListbox";
import { SelectTrigger } from "./SelectTrigger";
import { useSelectKeyboard } from "./useSelectKeyboard";
import { useSelectList } from "./useSelectList";
import { useSelectValue } from "./useSelectValue";

export type { SelectChangeEvent, SelectOption } from "./selectOption";
export type { SelectProps } from "./selectProps";
export { filterOptions, SEARCH_THRESHOLD } from "./filterOptions";
export { edgeEnabled, matchPrefix, stepEnabled } from "./optionNavigation";
export { optionsFromChildren } from "./optionsFromChildren";

const DEFAULT_PLACEHOLDER = "Choose an option";
const DEFAULT_SEARCH_PLACEHOLDER = "Search";

/**
 * The app's own dropdown (never the operating system's list): a button styled like the inputs that
 * opens a listbox in a portal. Keyboard per the WAI-ARIA select-only combobox: ↑↓ Home End, Enter
 * and Space, Escape, typeahead; long lists get a search box. Works inside `Field` (label, error).
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options: optionsProp,
    children,
    value: valueProp,
    defaultValue,
    onChange,
    onValueChange,
    size = "md",
    disabled,
    name,
    required,
    id,
    className,
    title,
    placeholder = DEFAULT_PLACEHOLDER,
    searchable: searchableProp,
    searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
    autoFocus,
    onBlur,
    onFocus,
    ...aria
  },
  ref,
) {
  const t = useUiText();
  const field = useFieldContext();
  const options = useMemo(() => optionsProp ?? optionsFromChildren(children), [optionsProp, children]);
  const selection = useSelectValue({ options, value: valueProp, defaultValue, name, onChange, onValueChange });
  const { value, selectedIndex } = selection;
  const searchable = searchableProp ?? options.length > SEARCH_THRESHOLD;
  const list = useSelectList({ options, value, selectedIndex, disabled, searchable });

  function commit(option: SelectOption | undefined): void {
    if (!option || option.disabled) return;
    list.close();
    selection.change(option);
  }

  const keyboard = useSelectKeyboard({ list, options, selectedIndex, searchable, disabled, commit });
  const triggerOwnsActiveOption = list.open && !searchable && list.active >= 0;

  return (
    <>
      <SelectTrigger
        buttonRef={(el) => {
          list.triggerRef.current = el;
          assignRef(ref, el);
        }}
        id={id ?? field?.id}
        title={title}
        disabled={disabled}
        autoFocus={autoFocus}
        open={list.open}
        controls={list.open ? list.listId : undefined}
        activeDescendant={triggerOwnsActiveOption ? optionId(list.listId, list.active) : undefined}
        accessibility={{
          label: aria["aria-label"],
          labelledBy: aria["aria-labelledby"],
          describedBy: aria["aria-describedby"] ?? field?.describedBy,
          invalid: aria["aria-invalid"] ?? field?.invalid,
          required,
        }}
        className={["nb-select", "nb-select-trigger", size === "sm" ? "nb-select-sm" : null, className].filter(Boolean).join(" ")}
        selected={selection.selected}
        placeholder={t(placeholder)}
        onClick={() => (list.open ? list.close() : list.openList())}
        onKeyDown={keyboard.onTriggerKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {name ? <input type="hidden" name={name} value={value} /> : null}
      <SelectListbox
        list={list}
        value={value}
        search={searchable ? { placeholder: t(searchPlaceholder), onKeyDown: keyboard.onSearchKeyDown } : undefined}
        label={aria["aria-label"]}
        labelledBy={aria["aria-label"] ? undefined : (aria["aria-labelledby"] ?? (field ? `${field.id}-label` : undefined))}
        onCommit={commit}
      />
    </>
  );
});
